import React from 'react';
import { TFunction } from 'i18next';
import { Documentation } from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import { CellProps, Column } from '@lib/components/Table/types';
import { getParkedReasonLabel } from '@lib/utils/indexingFailureReasonCopy';
import DocumentCategoryCell from 'routes/Documentation/DocumentList/utils/DocumentCategoryCell';
import DocumentCodeCell from 'routes/Documentation/DocumentList/utils/DocumentCodeCell';
import DocumentNameCell from 'routes/Documentation/DocumentList/utils/DocumentNameCell';
import { IndexingDocumentationItem } from './types';

interface Args {
  // Opens one document — the caller sends it to a new tab, since this modal
  // sits on the Company plan page and closing it to navigate would lose both
  // the list and any action beside it.
  onOpen: (item: Documentation) => void;
  t: TFunction;
  // `aiIndexingError` is "null while nothing has failed" — SKIPPED documents
  // processed successfully and simply yielded no searchable text, so every row
  // would fall back to the same generic "other" copy. Parked/failed lists (a
  // subset and superset of the same failure vocabulary) keep the column; the
  // not-indexable list omits it rather than imply a failure that didn't happen.
  includeReasonColumn?: boolean;
}

// The three shared cells are typed against the full generated `Documentation`.
// aiIndexingDocumentations selects a strict subset of it, which codegen types
// as its own shape rather than a `Partial<Documentation>` — so the hand-off
// needs a widening cast. Safe because the subset covers everything the cells
// actually read with AI detection off (name + attachment, code, category); the
// fields left unselected sit behind `isAiDetectionEnabled`, which these modals
// never pass.
const asDocument = (item: IndexingDocumentationItem) =>
  item as unknown as Documentation;

// Column ids double as the keys of DocumentationSortInputObject (see
// getIndexingDocumentsSorts) — renaming one silently breaks sorting.
export default function getIndexingDocumentsTableColumns({
  onOpen,
  t,
  includeReasonColumn = true,
}: Args): Column<IndexingDocumentationItem>[] {
  const columns: Column<IndexingDocumentationItem>[] = [
    {
      id: 'name',
      Header: t('document'),
      // Reuses the document list's own name cell, so a parked row looks like the
      // row an admin already knows from the list — thumbnail, filename, size —
      // instead of bare text. The whole point of the modal: the banner said how
      // many, this says which, and each one opens where the file can be replaced.
      accessorFn: ({ item }) => item.name,
      Cell: (props: CellProps<IndexingDocumentationItem>) => {
        const { item } = props;
        // DocumentationPreview renders NOTHING without an attachment, and both
        // currentVersion and attachmentAttached are nullable — a row that hit
        // that would show a nameless document in the one list whose job is to
        // name them. Fall back to plain text.
        if (!item.currentVersion?.attachmentAttached) {
          return <TextCell {...props} />;
        }
        return <DocumentNameCell item={asDocument(item)} onOpen={onOpen} />;
      },
      minWidth: 320,
      sortable: true,
    },
    {
      id: 'code',
      Header: t('code'),
      accessorFn: ({ item }) => item.code,
      Cell: ({ item }: CellProps<IndexingDocumentationItem>) => (
        <DocumentCodeCell item={asDocument(item)} />
      ),
      width: 120,
      minWidth: 120,
    },
    {
      id: 'categoryName',
      Header: t('category'),
      Cell: ({ item }: CellProps<IndexingDocumentationItem>) => (
        <DocumentCategoryCell item={asDocument(item)} />
      ),
      minWidth: 120,
      maxWidth: 250,
    },
  ];

  if (includeReasonColumn) {
    columns.push({
      id: 'aiIndexingError',
      Header: t('reason'),
      // Names the action rather than the failure — an unreadable file needs
      // replacing, a missing one re-uploading, and neither is what the Retry
      // button beside the list does on its own.
      accessorFn: ({ item }) => getParkedReasonLabel(item.aiIndexingError, t),
      Cell: (props: CellProps<IndexingDocumentationItem>) => (
        <TextCell {...props} />
      ),
      minWidth: 280,
    });
  }

  return columns;
}
